import { inject, Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { injectQuery } from './query'
import { ToastService } from './toast.service'
import { LoggerService } from './logger.service'
import { User } from './users.service'
import { Organization } from './organizations.service'
import { Call, CallProcessingStatus, CallStatus, CallType, MetricType } from './calls.service'
import { queryKeys } from './query-keys'
import { TimeInterval } from './common/time-interval/time-interval.component'
import { catchError, EMPTY, map, Observable, tap } from 'rxjs'
import { generateHeadersWithTimezone } from './utils'
import { DownloadsService } from './downloads.service'
import type { SupportUserActivityResult, UserActivityRow } from '../../../backend/src/metrics/support-user-activity-api.dto'
import type { SlaMetricsResult, SlaOrganizationBreakdown, SlaProcessingDistributionBucket } from '../../../backend/src/metrics/sla-api.dto'
import type { ActivityLogCoverage, ActivityLogDetails, ActivityLogEntry, ActivityLogFieldChange } from '../../../backend/src/metrics/activity-log-api.dto'
import type { SubscriptionUsageResult } from '../../../backend/src/metrics/subscription-usage-api.dto'

export type { SlaMetricsResult, SlaOrganizationBreakdown, SlaProcessingDistributionBucket }
export type { SubscriptionUsageResult }
export {
  MAX_USAGE_ALERT_THRESHOLD_COUNT,
  MAX_USAGE_ALERT_THRESHOLD_PERCENTAGE,
  MIN_USAGE_ALERT_THRESHOLD_PERCENTAGE,
} from '../../../backend/src/metrics/subscription-usage-api.dto'
export { ACTIVITY_LOG_AUTOMATIC_PERSON, ACTIVITY_LOG_SUPPORT_PERSON } from '../../../backend/src/metrics/activity-log-api.dto'
export type { ActivityLogCoverage, ActivityLogDetails, ActivityLogEntry, ActivityLogFieldChange }

export type AdminMetricQueryResult = {
  list: MetricDataPoint[]
  total: number
}

export type AdminCompletedCallsQueryResult = {
  list: CompletedCallDetails[]
  total: number
}

export enum MetricComponent {
  USER = 'USER',
  BOT = 'BOT',
  BACKEND = 'BACKEND',
  CALENDAR_SYNCHRONIZATION = 'CALENDAR_SYNCHRONIZATION',
  BOT_MANAGER = 'BOT_MANAGER',
  CALL_DELETER = 'CALL_DELETER',
}

export type MetricDataPoint = {
  id: number
  userId: number | null
  user: User | null
  callId: number | null
  call: Call | null
  organizationId: number | null
  organization: Organization | null
  component: MetricComponent
  metric: MetricType
  metadata: any
  timestamp: Date
}

/**
 * What the usage row says about the user, which is all the endpoint sends: the table draws a name,
 * a link to the organization and an active chip out of it, and the whole user row costs several
 * megabytes once every organization of every client is asked about.
 */
export type UsageMetricUser = {
  id: number
  firstName: string
  lastName: string
  organization: { id: number; name: string }
}

export type UsageByUserMetric = {
  user: UsageMetricUser
  usageByInterval: {
    intervalLabel: string
    intervalType: 'DAY' | 'MONTH'
    duration: number | null
    count: number | null
  }[]
  totalDuration: number | null
  totalCount: number | null
  active: boolean
}

export type RawUsageByUserMetric = {
  [userId: string]: UsageByUserMetric
}

export type UsageByOrganizationMetric = {
  organization: Organization
  activeUserCount: number
  usageByInterval: {
    intervalLabel: string
    intervalType: 'DAY' | 'MONTH'
    duration: number | null
    count: number | null
  }[]
}

export type RawUsageByOrganizationMetric = {
  [organizationId: string]: UsageByOrganizationMetric
}

export function normalizeUsageByUserMetric(data: RawUsageByUserMetric): UsageByUserMetric[] {
  return Object.values(data).map((value) => ({
    user: value.user,
    usageByInterval: Object.entries(value.usageByInterval).map(([intervalLabel, data]: [string, any]) => ({
      intervalLabel,
      ...data,
    })),
    totalDuration: value.totalDuration,
    totalCount: value.totalCount,
    active: value.active,
  }))
}

export function normalizeOrganizationUsageMetric(data: RawUsageByOrganizationMetric): UsageByOrganizationMetric[] {
  return Object.values(data).map((value) => ({
    organization: value.organization,
    activeUserCount: value.activeUserCount,
    usageByInterval: Object.entries(value.usageByInterval).map(([intervalLabel, data]: [string, any]) => ({
      intervalLabel,
      ...data,
    })),
  }))
}

export type CompletedCallUser = {
  userId: number
  firstName: string
  lastName: string
  email: string
  completedCallId: number
}

export type DeletionLifecycleStage = 'INITIAL_RETENTION' | 'SOFT_DELETED' | 'HARD_DELETED'

export type CompletedCallDetails = {
  callId: number
  name: string
  organizationId: number
  duration: number | null
  actualStartDate: Date | null
  status: CallStatus
  type: CallType
  users: CompletedCallUser[]
  latestCallSessionStatus: string | null
  processingStatus: CallProcessingStatus | null
  avgConfidenceScore: number | null
  avgRms: number | null
  avgHfRatio: number | null
  meetingRoomNames: string[]
  deletionLifecycleStage: DeletionLifecycleStage
}

export type FeatureAdoptionForUser = {
  userId: number
  firstName: string
  lastName: string
  hasUploadedAgenda: boolean
  hasLabelledSpeaker: boolean
  hasUsedEditor: boolean
  hasUsedAiActions: boolean
  hasEnabledSpeakerLabelingMode: boolean
  hasChangedMinutingStyles: boolean
  hasUsedJoinNow: boolean
  hasUsedPausing: boolean
}

export type CallScore = {
  callId: number
  name: string
  organizationId: number
  avgConfidenceScore: number
}

export type MeetingRoomMetric = {
  id: number
  canonicalName: string
  organizationId: number
  averageConfidenceScore: number | null
  averageRms: number | null
  averageHfRatio: number | null
  callCount: number
}

export type EngagementForUser = {
  userId: number
  firstName: string
  lastName: string
  callCount: number
  callsViewed: number
  callsEdited: number
  callsSpeakerLabeled: number
  callsExported: number
  averageSecondsToEdit: number
}

export type PerformanceMetric = {
  name: string
  sampleCount: number
  avgLatencyMs: number
  minLatencyMs: number
  maxLatencyMs: number
  p50LatencyMs: number
  p95LatencyMs: number
  p99LatencyMs: number
}

export type ActivityPrecision = 'HOUR' | 'DAY' | 'WEEK'

export type ActivityDataPoint = {
  intervalLabel: string
  activeUsers: number
  callViews: number
  appOpens: number
  editorSaves: number
}

export type ActivityMetricsResult = {
  precision: ActivityPrecision
  dataPoints: ActivityDataPoint[]
}

export type { SupportUserActivityResult, UserActivityRow }

@Injectable({
  providedIn: 'root',
})
export class MetricsService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  toastService = inject(ToastService)
  loggerService = inject(LoggerService)
  downloadsService = inject(DownloadsService)

  constructor() {}

  sendMetric(callId: number | null, type: MetricType, metadata: object = {}) {
    const params = {
      type,
      callId,
      ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
    }
    this.httpClient.post<void>(`/api/metrics`, params).subscribe({
      error: (err) => {
        this.loggerService.error(`Failed to send metric ${type} for call ${callId}.`, err)
      },
    })
  }

  getMetrics(
    userIds: number[],
    callId: number | null,
    organizationId: number | null,
    component: MetricComponent | null,
    metrics: MetricType[],
    startDate: Date | null,
    endDate: Date | null,
    sortField: string | null,
    sortDirection: string | null,
    page: number,
    pageSize: number
  ) {
    const params = {
      ...(userIds.length > 0 ? { userId: userIds.map(String) } : {}),
      ...(callId ? { callId: callId.toString() } : {}),
      ...(organizationId ? { organizationId: organizationId.toString() } : {}),
      ...(component ? { component } : {}),
      ...(metrics.length > 0 ? { metric: metrics } : {}),
      ...(startDate ? { startDate: startDate.toISOString() } : {}),
      ...(endDate ? { endDate: endDate.toISOString() } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      page,
      pageSize,
    }
    return this.query({
      queryKey: queryKeys.metrics.list(params),
      queryFn: () =>
        this.httpClient.get<AdminMetricQueryResult>('/api/metrics', {
          params,
        }),
    }).result$
  }

  /**
   * When each action the activity log reports on started being recorded, so an empty result can be
   * told apart from an action that never happened.
   */
  getActivityLogCoverage() {
    return this.query({
      queryKey: queryKeys.metrics.activityLogCoverage(),
      queryFn: () => this.httpClient.get<ActivityLogCoverage>('/api/metrics/activity-log/coverage'),
    }).result$
  }

  getUsageByUserMetrics(
    type: string,
    userId: number | null,
    organizationIds: number[],
    exclusionFilters: string[],
    viewFilter: string | null,
    fromDate: Date | null
  ) {
    const params = {
      ...(userId ? { userId } : {}),
      ...(organizationIds !== null && organizationIds.length > 0 ? { organizationIds: organizationIds } : {}),
      ...(exclusionFilters.length > 0 ? { exclusionFilters } : {}),
      ...(viewFilter ? { viewFilter } : {}),
      ...(fromDate ? { fromDateString: fromDate.toISOString() } : {}),
    }
    return this.query({
      queryKey: queryKeys.metrics.usageByUser(type, params),
      queryFn: () => this.httpClient.get<RawUsageByUserMetric>('/api/metrics/usage-by-user', { params }).pipe(map((data) => normalizeUsageByUserMetric(data))),
    }).result$
  }

  getUsageByOrganizationMetrics(organizationIds: number[], fromDate: Date | null) {
    const params = {
      ...(organizationIds !== null && organizationIds.length > 0 ? { organizationIds: organizationIds } : {}),
      ...(fromDate ? { fromDateString: fromDate.toISOString() } : {}),
    }

    return this.query({
      queryKey: queryKeys.metrics.usageByOrganization(params),
      queryFn: () => this.httpClient.get<any>('/api/metrics/usage-by-organization', { params }).pipe(map((data) => normalizeOrganizationUsageMetric(data))),
    }).result$
  }

  getCompletedCalls(
    userId: number | null,
    organizationIds: number[],
    exclusionFilters: string[],
    viewFilter: string | null,
    timeInterval: TimeInterval,
    nameQuery: string | null,
    sortField: string | null,
    sortDirection: string | null,
    page: number,
    pageSize: number
  ) {
    const params = {
      ...(userId ? { userId: userId.toString() } : {}),
      ...(organizationIds !== null && organizationIds.length > 0 ? { organizationIds: organizationIds } : {}),
      ...(exclusionFilters.length > 0 ? { exclusionFilters } : {}),
      ...(timeInterval.from ? { fromDateString: timeInterval.from.toISOString() } : {}),
      ...(timeInterval.to ? { toDateString: timeInterval.to.toISOString() } : {}),
      ...(viewFilter ? { viewFilter } : {}),
      ...(nameQuery ? { nameQuery } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      page,
      pageSize,
    }
    return this.query({
      queryKey: queryKeys.metrics.completedCalls(params),
      queryFn: () =>
        this.httpClient.get<AdminCompletedCallsQueryResult>('/api/metrics/completed-calls', {
          params,
        }),
    }).result$
  }

  getFeatureAdoptionByUser(organizationIds: number[]) {
    return this.query({
      queryKey: queryKeys.metrics.featureAdoptionByUser(organizationIds),
      queryFn: () =>
        this.httpClient.get<FeatureAdoptionForUser[]>('/api/metrics/feature-adoption-by-user', {
          params: { organizationIds },
        }),
    }).result$
  }

  getEngagementByUser(organizationIds: number[]) {
    return this.query({
      queryKey: queryKeys.metrics.engagementByUser(organizationIds),
      queryFn: () =>
        this.httpClient.get<EngagementForUser[]>('/api/metrics/engagement-by-user', {
          params: { organizationIds },
        }),
    }).result$
  }

  getPerformanceMetrics(organizationIds: number[], startDate: Date | null, endDate: Date | null) {
    const startDateString = startDate !== null ? startDate.toISOString() : null
    const endDateString = endDate !== null ? endDate.toISOString() : null
    const params = {
      ...(organizationIds.length > 0 ? { organizationIds } : {}),
      ...(startDateString !== null ? { startDateString } : {}),
      ...(endDateString !== null ? { endDateString } : {}),
    }
    return this.query({
      queryKey: queryKeys.metrics.performance(organizationIds, startDateString, endDateString),
      queryFn: () => this.httpClient.get<PerformanceMetric[]>('/api/metrics/performance', { params }),
    }).result$
  }

  getActivityMetrics(organizationIds: number[], startDate: Date, endDate: Date, precision: ActivityPrecision) {
    const startDateIso = startDate.toISOString()
    const endDateIso = endDate.toISOString()
    const params = {
      ...(organizationIds.length > 0 ? { organizationIds } : {}),
      startDate: startDateIso,
      endDate: endDateIso,
      precision,
    }
    return this.query({
      queryKey: queryKeys.metrics.activity(organizationIds, startDateIso, endDateIso, precision),
      queryFn: () => this.httpClient.get<ActivityMetricsResult>('/api/metrics/activity', { params }),
    }).result$
  }

  getSlaMetrics(organizationIds: number[], month: string) {
    const params = {
      ...(organizationIds.length > 0 ? { organizationIds } : {}),
      month,
    }
    return this.query({
      queryKey: queryKeys.metrics.sla(organizationIds, month),
      queryFn: () => this.httpClient.get<SlaMetricsResult>('/api/metrics/sla', { params }),
    }).result$
  }

  exportSlaMetrics(organizationIds: number[], month: string): Observable<void> {
    const params = {
      ...(organizationIds.length > 0 ? { organizationIds } : {}),
      month,
    }
    return this.httpClient.get('/api/metrics/sla/export', { params, responseType: 'blob', headers: generateHeadersWithTimezone() }).pipe(
      tap((data) => {
        this.downloadsService.downloadData(data, `sla-dashboard-${month}.pdf`, 'pdf')
        this.toastService.show('CALLS.EXPORT_COMPLETED')
      }),
      catchError((error) => {
        // A 4xx is the caller's own doing and a 503 means the renderer is busy, both of which the retry advice
        // in the toast already covers. Only an unexpected server failure is worth an error.
        const isExpected = error instanceof HttpErrorResponse && (error.status < 500 || error.status === 503)
        const message = 'Exporting the SLA dashboard failed.'
        if (isExpected) {
          this.loggerService.warn(message, error)
        } else {
          this.loggerService.error(message, error)
        }
        this.toastService.show('CALLS.EXPORT_FAILED')
        return EMPTY
      }),
      map(() => void 0)
    )
  }

  /**
   * The caller's client measured against its subscription. Answers null where support has recorded
   * no contract, which is what leaves the subscription period off the dashboard.
   */
  getSubscriptionUsage() {
    return this.query({
      queryKey: queryKeys.metrics.subscriptionUsage(),
      queryFn: () => this.httpClient.get<SubscriptionUsageResult | null>('/api/metrics/subscription-usage'),
    }).result$
  }

  getMeetingRoomMetrics(organizationIds: number[]) {
    const params = {
      ...(organizationIds.length > 0 ? { organizationIds } : {}),
    }
    return this.query({
      queryKey: queryKeys.metrics.meetingRoomMetrics(organizationIds),
      queryFn: () => this.httpClient.get<MeetingRoomMetric[]>('/api/metrics/meeting-rooms', { params }),
    }).result$
  }

  exportCompletedCalls(
    userId: number | null,
    organizationIds: number[],
    exclusionFilters: string[],
    viewFilter: string | null,
    timeInterval: TimeInterval,
    nameQuery: string | null,
    sortField: string | null,
    sortDirection: string | null,
    includeAudioMetrics: boolean,
    includeDetailedAudioMetrics: boolean
  ): Observable<void> {
    const fileName = `completed-calls-from-${timeInterval.from?.toISOString()}${timeInterval.to ? `-to-${timeInterval.to.toISOString()}` : ''}.csv`
    const params = {
      ...(userId ? { userId: userId.toString() } : {}),
      ...(organizationIds !== null && organizationIds.length > 0 ? { organizationIds: organizationIds } : {}),
      ...(exclusionFilters.length > 0 ? { exclusionFilters } : {}),
      ...(timeInterval.from ? { fromDateString: timeInterval.from.toISOString() } : {}),
      ...(timeInterval.to ? { toDateString: timeInterval.to.toISOString() } : {}),
      ...(viewFilter ? { viewFilter } : {}),
      ...(nameQuery ? { nameQuery } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      includeAudioMetrics: includeAudioMetrics ? 'true' : 'false',
      includeDetailedAudioMetrics: includeDetailedAudioMetrics ? 'true' : 'false',
    }
    return this.httpClient
      .get(`/api/metrics/completed-calls/export`, {
        params,
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, 'csv')
          this.toastService.show('CALLS.EXPORT_COMPLETED')
        }),
        catchError((error) => {
          console.error('Export failed:', error)
          this.toastService.show('CALLS.EXPORT_FAILED')
          return EMPTY
        }),
        map(() => void 0)
      )
  }
}
